/* eslint-disable max-lines */

import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { oc } from 'ts-optchain';
import {
  AuthApi,
  DatacenterApi,
  EnvironmentApi,
  SettingsApi
} from 'vrlcm-gui-api';
import {
  EnvironmentRequest,
  EnvironmentRequestProduct,
  EnvironmentState,
  RequestType,
  User,
  VCenter,
  Vrops
} from 'vrlcm-gui-types';
import {
  gte,
  isVcfEnabledEnvironmentWithVcfUser,
  safeJsonParse,
  toSafeSelectedProducts
} from 'vrlcm-gui-utils';
import {
  initializeStepProductsFailed,
  initializeStepProductsSuccess
} from '../../../actions/workflows/steps/Products';
import ProductStepAPI from '../../../api/workflows/steps/Product';
import * as Constants from '../../../constants/action-constants';
import { StoreState } from '../../../types';
import { DisplayGroupMetadata, DisplayMetadata } from '../../../types/Common';
import { UIEnvironmentState } from '../../../types/Environments';
import {
  ProductDisplayMetadata,
  ProductMetadata
} from '../../../types/workflows/steps/Product';
import { getVropsCa } from '../../../utils/UtilFunctions';
import { getVropsCaNodes } from '../util';

const getEnvironmentWorkflowState = (state: StoreState) =>
  state.environmentWorkflow.state;

const getCompletedEnvironments = (state: StoreState) =>
  state.environments.items.completed;

function* initializeStepProducts() {
  try {
    const environmentWorkflowState: EnvironmentState = yield select(
      getEnvironmentWorkflowState
    );

    const isAddProduct =
      environmentWorkflowState.requestType === RequestType.ADD_PRODUCT;
    const isAddNode =
      environmentWorkflowState.requestType === RequestType.ADD_NODE;
    const isInstallFlow =
      [
        RequestType.VALIDATE_AND_CREATE_ENVIRONMENT,
        RequestType.CREATE_ENVIRONMENT,
        RequestType.VALIDATE_CREATE_ENVIRONMENT
      ].indexOf(environmentWorkflowState.requestType) > -1;

    const {
      environmentRequestDTO: environmentRequest
    } = environmentWorkflowState;
    let selectedProducts = toSafeSelectedProducts(environmentRequest);

    let existingProducts: EnvironmentRequestProduct[] = [];
    if (isAddProduct) {
      const existingEnvironmentState: EnvironmentRequest = yield call(
        EnvironmentApi.getEnvironmentRequestById,
        environmentRequest.environmentId
      );
      existingProducts = existingEnvironmentState.products;
    }

    const allProducts = selectedProducts.map(
      p => `${p.id}:${p.selectedVersion}`
    );

    selectedProducts = selectedProducts.filter(p => !p.disabled);

    const selectedVssc = selectedProducts.find(p => {
      return p.id === 'vssc';
    });

    const selectedVsscTenantId = selectedVssc ? selectedVssc.tenantId : '';

    const vsscVersionSupportingIntegration = !!(
      selectedVssc &&
      selectedVssc.selectedVersion &&
      gte(selectedVssc.selectedVersion, '8.4.2')
    );

    const vropsCa = getVropsCa(selectedProducts);
    const stateProducts = environmentRequest.products;
    const infrastructure = environmentRequest.infrastructure.properties;

    const allVCenters: VCenter[] = yield call(
      DatacenterApi.getVCenters,
      infrastructure.dataCenterVmid
    );

    const vropsExists = selectedProducts.find(p => p.id === 'vrops');
    let vropsLogForwardingEntities: string[] = [];
    if (vropsExists) {
      const logForwardingEntitiesString: string = yield call(
        SettingsApi.getAccountLockoutValues,
        'lcm.plugin.vrops.log.entities'
      );
      vropsLogForwardingEntities = logForwardingEntitiesString.split(',');
      vropsLogForwardingEntities = vropsLogForwardingEntities.filter(
        logForwardingEntity => {
          if (
            vropsExists.selectedVersion &&
            gte(vropsExists.selectedVersion, '8.10.0') &&
            logForwardingEntity === 'CASSANDRA'
          ) {
            return false;
          }
          return true;
        }
      );
    }

    const vCenters = allVCenters.filter(
      vCenter => vCenter.vcDataCollectionStatus === 'SUCCESS'
    );

    const vraImport = selectedProducts.find(
      p => p.id === 'vra' && p.type === 'import'
    );
    let existingVidm: EnvironmentRequestProduct | undefined;
    if (vraImport) {
      try {
        const globalenvironment = yield call(
          EnvironmentApi.getEnvironmentDataById,
          'globalenvironment'
        );
        const environmentData = safeJsonParse<EnvironmentRequest>(
          globalenvironment.environmentData
        )!;
        existingVidm =
          globalenvironment.status !== 'DELETED'
            ? environmentData.products.find(p => p.id === 'vidm')
            : undefined;
      } catch (e) {
        existingVidm = undefined;
      }
    }

    const user: User = yield call(AuthApi.whoAmI);

    const isVcfEnvironment = isVcfEnabledEnvironmentWithVcfUser(
      environmentRequest,
      user
    );

    const completedEnvironments: UIEnvironmentState[] = yield select(
      getCompletedEnvironments
    );
    const isVidmInstalled = completedEnvironments.some(
      env => env.environmentRequestDTO.environmentId === 'globalenvironment'
    );

    const environmentsWithVrops = completedEnvironments.filter(env =>
      env.environmentRequestDTO.products.some(product => product.id === 'vrops')
    );

    const productsMetadata: ProductMetadata[] = yield all(
      selectedProducts.map(async p => {
        const currentProduct = stateProducts.find(
          cp => cp.id === p.id
        ) as EnvironmentRequestProduct;
        let metadata;
        if (
          p.selectedDeploymentType !== 'config' &&
          p.selectedAuthenticationType !== 'config' &&
          currentProduct.nodes.length === 0
        ) {
          metadata = await ProductStepAPI.getProductsMetadata(
            p.id,
            p.selectedVersion || '',
            p.selectedDeploymentType || '',
            p.type === 'import',
            allProducts,
            (p as Vrops).nodeCount,
            (p as Vrops).haEnabled,
            isVcfEnvironment,
            p.selectedAuthenticationType || ''
          );
          if (p.id === 'vrops') {
            metadata.componentList.collectorgroups = {
              countNotAllowed: 'null',
              maxNodeCount: 'n',
              minNodeCount: '0'
            };
          }
        } else {
          const productProperties = await ProductStepAPI.getProductPropertiesMetadata(
            p.id,
            p.selectedVersion || '',
            allProducts,
            p.type === 'import',
            isVcfEnvironment
          );
          let componentList = await ProductStepAPI.getNodesCardinality(
            p.id,
            p.selectedVersion || ''
          );

          if (p.id === 'vrops') {
            componentList.collectorgroups = {
              countNotAllowed: 'null',
              maxNodeCount: 'n',
              minNodeCount: '0'
            };
          }

          const productNodes =
            p.id === 'vrops' && vropsCa
              ? []
              : await Promise.all(
                  currentProduct.nodes.map(n => {
                    return ProductStepAPI.getNodeDefaultPropertiesMetadata(
                      p.id,
                      n.type
                    );
                  })
                );
          const clusterVip =
            p.id === 'vrops' && vropsCa
              ? []
              : await ProductStepAPI.getClusterVipMetadata(
                  p.id,
                  p.selectedVersion || ''
                );
          metadata = {
            productName: p.id,
            productVersion: p.selectedVersion,
            deploymentType: p.selectedDeploymentType,
            componentList,
            clusterVip,
            productProperties,
            productNodes
          };
        }
        if (!isVidmInstalled) {
          metadata.productProperties[0].properties = metadata.productProperties[0].properties.map(
            prop => {
              if (prop.name === 'vraImportVersion') prop.defaultValue = '7.6.0';
              return prop;
            }
          );
        }
        return metadata;
      })
    );

    let displayMetadata: DisplayGroupMetadata[] = yield all(
      selectedProducts.map(p => {
        return call(
          ProductStepAPI.getProductPropertiesDisplayMetadata,
          p.id,
          allProducts,
          p.type === 'import',
          isInstallFlow,
          isVcfEnvironment,
          isAddProduct,
          isVidmInstalled,
          existingProducts,
          environmentsWithVrops,
          selectedVsscTenantId,
          vsscVersionSupportingIntegration,
          p.selectedAuthenticationType
        );
      })
    );

    displayMetadata = selectedProducts.map((p, i) => {
      if (displayMetadata[i].properties.length === 0) {
        return {
          name: p.id,
          properties: productsMetadata[i].productProperties[0].properties.map<
            DisplayMetadata
          >((prop, j) => ({
            name: prop.name,
            displayRules: [],
            order: j,
            validationRules: []
          }))
        };
      }
      return displayMetadata[i];
    });

    const productsDisplayMetadata: ProductDisplayMetadata[] = yield all(
      displayMetadata.map(async (p, i) => {
        let nodes: DisplayGroupMetadata[] = [];
        if (!(p.name === 'vrops' && vropsCa)) {
          nodes = await Promise.all(
            productsMetadata[i].productNodes.map(n => {
              const node = ProductStepAPI.getNodeDisplayMetadata(
                p.name,
                n.name
              );
              return node;
            })
          );
        }
        return {
          ...p,
          nodes
        };
      })
    );
    const products = stateProducts.map((p, i) => {
      return {
        ...p,
        properties: productsMetadata[i].productProperties[0].properties.reduce(
          (acc, cur) => {
            const vrbAuthType =
              infrastructure.masterVidmEnabled === 'true'
                ? 'vidm'
                : 'standalone';
            let infraMappedProperty =
              cur.dataType === 'vm.lcm.password'
                ? infrastructure.defaultPassword
                : cur.name === 'vrbAuthType'
                ? vrbAuthType
                : infrastructure[cur.name];
            if (p.id === 'vro' && cur.name === 'vidmTenantAdminPassword') {
              infraMappedProperty = '';
            }
            const value = oc(stateProducts[i]).properties[cur.name]();
            return {
              ...acc,
              [cur.name]:
                value !== undefined && value !== null && value !== ''
                  ? value
                  : infraMappedProperty || cur.defaultValue
            };
          },
          Object.entries(p.properties).reduce((acc, [key, value]) => {
            if (key.startsWith('__')) {
              return {
                ...acc,
                [key]: value
              };
            }
            if (key === 'authenticationType') {
              return {
                ...acc,
                [key]: value
              };
            }
            return acc;
          }, {})
        ),
        nodes:
          p.id === 'vrops' && vropsCa
            ? []
            : productsMetadata[i].productNodes.map((node, j) => {
                const pNode = p.nodes[j];
                return {
                  type: node.name,
                  properties: {
                    ...node.properties.reduce((acc, cur) => {
                      return {
                        ...acc,
                        [cur.name]: oc(stateProducts[i]).nodes[j].properties[
                          cur.name
                        ](cur.defaultValue)
                      };
                    }, {}),
                    ...(pNode && pNode.properties)
                  }
                };
              })
      };
    });
    if (isAddNode) {
      products[0].properties['snapshot:takeSnapshot'] =
        stateProducts[0].properties['snapshot:takeSnapshot'];
      products[0].properties['snapshot:isRetainSnapshot'] =
        stateProducts[0].properties['snapshot:isRetainSnapshot'];
    }
    if (vropsCa) {
      const vropsStateProduct = stateProducts.find(p => p.id === vropsCa.id);
      const vropsProduct = products.find(p => p.id === vropsCa.id);
      if (vropsProduct && vropsStateProduct) {
        const dataNodeCount = vropsCa.nodeCount - 2;
        const dataNodePairCount = dataNodeCount / 2;
        const vropsCaNodes = getVropsCaNodes(
          vropsStateProduct,
          dataNodePairCount
        );
        vropsProduct.nodes = vropsCaNodes;
      }
    }

    const vropsStateProduct = stateProducts.find(p => p.id === 'vrops');
    const vropsProduct = products.find(p => p.id === 'vrops');
    if (
      vropsStateProduct &&
      vropsStateProduct.properties?.adapterFiles &&
      vropsProduct
    ) {
      vropsProduct.properties['adapterFiles'] =
        vropsStateProduct.properties?.adapterFiles;
    }

    const vCentersDetails = yield all(
      vCenters.map(vCenter => {
        return call(
          DatacenterApi.getVCenter,
          infrastructure.dataCenterVmid,
          vCenter.vCenterName
        );
      })
    );
    yield put(
      initializeStepProductsSuccess(
        products,
        vCentersDetails,
        productsMetadata,
        productsDisplayMetadata,
        vropsLogForwardingEntities,
        existingVidm
      )
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Initialize product steps failed', e);
    yield put(initializeStepProductsFailed());
  }
}

function* watchForProducts() {
  yield takeLatest(Constants.INITIALIZE_STEP_PRODUCTS, initializeStepProducts);
}

export default function* productsSaga() {
  yield all([watchForProducts()]);
}
